import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, Phone, Mail, Loader2, ArrowLeft } from "lucide-react";
import { getPilot } from "@/lib/api";
import ServiceWriterForm from "@/components/ServiceWriterForm";
import Footer from "@/components/Footer";

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.65, delay: 0.12 + i * 0.1, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function PilotLandingPage({ demoBusiness, demoMode = false, demoActions = null }) {
  const { slug } = useParams();
  const [business, setBusiness] = useState(demoBusiness || null);
  const [loading, setLoading] = useState(!demoBusiness);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    // In demo mode, skip the API fetch — the parent passes a synthetic business.
    if (demoBusiness) {
      setBusiness(demoBusiness);
      setLoading(false);
      setNotFound(false);
      return;
    }
    let ignore = false;
    setLoading(true);
    setNotFound(false);
    getPilot(slug)
      .then((data) => {
        if (!ignore) setBusiness(data);
      })
      .catch((err) => {
        if (!ignore) {
          if (err?.response?.status === 404) setNotFound(true);
          setBusiness(null);
        }
      })
      .finally(() => !ignore && setLoading(false));
    return () => {
      ignore = true;
    };
  }, [slug]);

  if (loading) {
    return (
      <main
        className="min-h-screen w-full bg-[#0a0a0b] text-white/80 flex items-center justify-center"
        data-testid="pilot-loading"
      >
        <div className="flex items-center gap-3 font-mono-plex text-[11px] uppercase tracking-[0.3em] text-white/40">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading pilot…
        </div>
      </main>
    );
  }

  if (notFound || !business) {
    return (
      <main
        className="min-h-screen w-full bg-[#0a0a0b] text-white/80 flex items-center justify-center px-6"
        data-testid="pilot-not-found"
      >
        <div className="max-w-md text-center">
          <div className="font-mono-plex text-[10px] uppercase tracking-[0.3em] text-white/40 mb-6">
            404 · No pilot at /{slug}
          </div>
          <h1 className="font-serif-display text-white text-4xl leading-tight mb-6">
            This pilot doesn&apos;t exist yet.
          </h1>
          <p className="font-mono-plex text-sm text-white/50 mb-10 leading-relaxed">
            The business you&apos;re looking for hasn&apos;t been provisioned on Our Local Stories.
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 border border-white/20 px-4 py-2 font-mono-plex text-[11px] uppercase tracking-[0.28em] text-white/70 hover:bg-white hover:text-black transition-colors"
            data-testid="pilot-not-found-home-link"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back to Our Local Stories
          </Link>
        </div>
      </main>
    );
  }

  const accent = business.accent_color || "#c9a24a";

  return (
    <main
      data-testid={demoMode ? "pilot-page-demo" : "pilot-page"}
      data-pilot-slug={business.slug}
      data-demo-mode={demoMode ? "true" : "false"}
      className="relative min-h-screen w-full bg-[#0a0a0b] text-white/90 overflow-hidden"
      style={{ "--accent": accent }}
    >
      {/* Demo mode ribbon + actions */}
      {demoMode && (
        <div
          className="sticky top-0 z-30 border-b border-white/10 bg-[#0a0a0b]/85 backdrop-blur"
          data-testid="demo-ribbon"
        >
          <div className="flex items-center justify-between gap-3 px-4 sm:px-6 py-2.5">
            <div className="flex items-center gap-2 min-w-0">
              <span
                className="h-1.5 w-1.5 rounded-full shrink-0"
                style={{ background: accent }}
              />
              <span className="font-mono-plex text-[10px] uppercase tracking-[0.28em] text-white/70 truncate">
                Live Demo — {business.name}
              </span>
            </div>
            {demoActions}
          </div>
        </div>
      )}
      {/* Ambient */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 z-0"
        style={{
          background: `radial-gradient(1000px 500px at 12% 8%, ${accent}18, transparent 60%), radial-gradient(900px 500px at 90% 90%, rgba(255,255,255,0.03), transparent 60%)`,
        }}
      />

      <div className="relative z-10 min-h-screen grid grid-cols-1 lg:grid-cols-12">
        {/* LEFT — Content */}
        <section
          className="col-span-1 lg:col-span-7 flex flex-col justify-between px-6 sm:px-10 md:px-16 lg:px-20 py-10 md:py-14 lg:py-16 min-h-screen"
          data-testid="pilot-hero-section"
        >
          {/* Top bar */}
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              {business.logo_url ? (
                <img
                  src={business.logo_url}
                  alt={`${business.name} logo`}
                  className="h-9 w-9 object-contain bg-white/5 border border-white/10"
                  data-testid="pilot-logo"
                />
              ) : (
                <div
                  className="h-9 w-9 flex items-center justify-center border border-white/15 bg-white/5 font-serif-display text-white/80 text-sm"
                  data-testid="pilot-logo-placeholder"
                  aria-label={`${business.name} logo placeholder`}
                >
                  {business.name
                    .split(" ")
                    .map((w) => w[0])
                    .slice(0, 2)
                    .join("")
                    .toUpperCase()}
                </div>
              )}
              <div className="font-mono-plex text-[10px] uppercase tracking-[0.28em] text-white/60">
                <div className="text-white/90" data-testid="pilot-business-name">
                  {business.name}
                </div>
                <div className="text-white/30 text-[9px] mt-0.5">{business.industry}</div>
              </div>
            </div>

            <div
              className="hidden sm:flex items-center gap-2 font-mono-plex text-[10px] uppercase tracking-[0.3em] text-white/40"
              data-testid="pilot-trial-badge"
            >
              <span className="h-1.5 w-1.5 rounded-full" style={{ background: accent }} />
              {business.trial_days_remaining > 0
                ? `${business.trial_days_remaining}-day pilot · in progress`
                : "Pilot period complete"}
            </div>
          </div>

          {/* Hero */}
          <div className="max-w-2xl w-full py-14 md:py-20">
            <motion.div
              variants={fadeUp}
              initial="hidden"
              animate="show"
              custom={0}
              className="font-mono-plex text-[11px] uppercase tracking-[0.28em] text-white/40 mb-6 flex items-center gap-3"
              data-testid="pilot-section-label"
            >
              <span className="h-px w-6" style={{ background: accent }} />
              <span>Powered by Our Local Stories</span>
            </motion.div>

            <motion.h1
              variants={fadeUp}
              initial="hidden"
              animate="show"
              custom={1}
              className="font-serif-display text-white text-4xl sm:text-5xl md:text-6xl leading-[1.02] tracking-tight"
              data-testid="pilot-headline"
            >
              {business.hero_headline || `${business.name}.`}
            </motion.h1>

            {business.hero_subheadline ? (
              <motion.p
                variants={fadeUp}
                initial="hidden"
                animate="show"
                custom={2}
                className="font-serif-display italic text-white/70 text-2xl md:text-3xl mt-5 leading-snug"
                data-testid="pilot-subheadline"
              >
                {business.hero_subheadline}
              </motion.p>
            ) : null}

            <motion.div
              variants={fadeUp}
              initial="hidden"
              animate="show"
              custom={3}
              className="mt-8 mb-6 h-px w-16"
              style={{ background: `${accent}80` }}
            />

            {business.tagline ? (
              <motion.p
                variants={fadeUp}
                initial="hidden"
                animate="show"
                custom={4}
                className="font-mono-plex text-sm md:text-[15px] text-white/55 max-w-xl leading-relaxed"
                data-testid="pilot-tagline"
              >
                {business.tagline}
              </motion.p>
            ) : null}

            {/* Services grid */}
            {business.services?.length ? (
              <motion.div
                variants={fadeUp}
                initial="hidden"
                animate="show"
                custom={5}
                className="mt-10"
                data-testid="pilot-services"
              >
                <div className="font-mono-plex text-[10px] uppercase tracking-[0.3em] text-white/35 mb-4">
                  Services
                </div>
                <ul className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-2">
                  {business.services.map((s, i) => (
                    <li
                      key={i}
                      className="font-mono-plex text-[12px] tracking-wide text-white/70 flex items-baseline gap-2"
                    >
                      <span className="text-white/25 text-[10px]">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span>{s}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ) : null}

            {/* Alfred — Service Director intro */}
            <motion.div
              variants={fadeUp}
              initial="hidden"
              animate="show"
              custom={6}
              className="mt-16 flex flex-col items-center text-center"
              data-testid="alfred-intro"
            >
              <div className="relative">
                {/* Ambient accent glow */}
                <div
                  aria-hidden
                  className="absolute inset-0 rounded-full blur-2xl opacity-40"
                  style={{ background: `radial-gradient(circle, ${accent}80 0%, transparent 70%)` }}
                />
                {/* Outer thin ring */}
                <div
                  aria-hidden
                  className="absolute -inset-[6px] rounded-full border"
                  style={{ borderColor: `${accent}30` }}
                />
                <img
                  src={`/alfred/${business.alfred_variant || "business_casual"}.png`}
                  alt="Alfred — Your Service Director"
                  data-testid="alfred-portrait"
                  data-alfred-variant={business.alfred_variant || "business_casual"}
                  loading="lazy"
                  className="relative h-32 w-32 sm:h-36 sm:w-36 rounded-full object-cover border border-white/15 bg-white/[0.03]"
                  style={{ boxShadow: `0 0 0 1px rgba(255,255,255,0.04), 0 12px 40px rgba(0,0,0,0.5)` }}
                />
              </div>
              <div
                className="mt-5 font-serif-display text-white text-lg"
                data-testid="alfred-name"
              >
                Alfred
              </div>
              <div
                className="mt-1 font-mono-plex text-[10px] uppercase tracking-[0.32em] text-white/45"
                data-testid="alfred-role"
              >
                Your Service Director
              </div>
              <p
                className="mt-4 font-mono-plex text-[12px] text-white/45 max-w-sm leading-relaxed"
                data-testid="alfred-copy"
              >
                Tell Alfred what&apos;s going on. He&apos;ll write it up for {business.name} before
                you even walk in.
              </p>
              <div
                aria-hidden
                className="mt-6 h-px w-10"
                style={{ background: `${accent}66` }}
              />
            </motion.div>

            {/* AI Service Writer form */}
            <motion.div
              variants={fadeUp}
              initial="hidden"
              animate="show"
              custom={7}
              className="mt-10"
            >
              <ServiceWriterForm business={business} demoMode={demoMode} />
            </motion.div>

            {/* Contact strip */}
            {(business.phone || business.email || business.address) && (
              <motion.div
                variants={fadeUp}
                initial="hidden"
                animate="show"
                custom={8}
                className="mt-14 border-t border-white/10 pt-8 grid grid-cols-1 sm:grid-cols-3 gap-6"
                data-testid="pilot-contact-strip"
              >
                {business.phone && (
                  <div className="flex items-start gap-3">
                    <Phone className="h-4 w-4 text-white/40 mt-0.5" />
                    <div>
                      <div className="font-mono-plex text-[10px] uppercase tracking-[0.28em] text-white/35">
                        Phone
                      </div>
                      <div className="font-mono-plex text-[13px] text-white/80 mt-1">
                        {business.phone}
                      </div>
                    </div>
                  </div>
                )}
                {business.email && (
                  <div className="flex items-start gap-3">
                    <Mail className="h-4 w-4 text-white/40 mt-0.5" />
                    <div>
                      <div className="font-mono-plex text-[10px] uppercase tracking-[0.28em] text-white/35">
                        Email
                      </div>
                      <div className="font-mono-plex text-[13px] text-white/80 mt-1 break-all">
                        {business.email}
                      </div>
                    </div>
                  </div>
                )}
                {business.address && (
                  <div className="flex items-start gap-3">
                    <MapPin className="h-4 w-4 text-white/40 mt-0.5" />
                    <div>
                      <div className="font-mono-plex text-[10px] uppercase tracking-[0.28em] text-white/35">
                        Location
                      </div>
                      <div className="font-mono-plex text-[13px] text-white/80 mt-1">
                        {business.address}
                      </div>
                    </div>
                  </div>
                )}
              </motion.div>
            )}
          </div>

          <Footer />
        </section>

        {/* RIGHT — Visual */}
        <aside
          className="hidden lg:block col-span-5 relative min-h-screen border-l border-white/5 overflow-hidden"
          aria-hidden
          data-testid="pilot-visual-panel"
        >
          <div
            className="absolute inset-0"
            style={{
              background: `linear-gradient(180deg, #0f0f11 0%, #0a0a0b 100%)`,
            }}
          />
          {/* Massive typographic mark */}
          <div className="absolute inset-0 flex items-center justify-center px-10">
            <div className="text-center">
              <div
                className="font-serif-display text-white/8 leading-none tracking-tighter select-none"
                style={{ fontSize: "clamp(120px, 22vw, 320px)", color: `${accent}12` }}
              >
                {business.name
                  .split(" ")
                  .map((w) => w[0])
                  .slice(0, 2)
                  .join("")
                  .toUpperCase()}
              </div>
              <div
                className="mt-8 font-mono-plex text-[11px] uppercase tracking-[0.35em]"
                style={{ color: `${accent}b3` }}
              >
                {business.city || business.industry}
              </div>
              <div className="mt-3 font-mono-plex text-[10px] uppercase tracking-[0.35em] text-white/25">
                Est. {new Date(business.trial_started_at).getFullYear()} · Pilot
              </div>
            </div>
          </div>

          <div className="absolute inset-8 border border-white/5 pointer-events-none" />

          <div
            className="absolute bottom-10 right-10 font-mono-plex text-[10px] uppercase tracking-[0.3em] text-white/35 text-right"
            data-testid="pilot-visual-marks"
          >
            <div>Our Local Stories LLC</div>
            <div className="text-white/20 mt-1">Technical Foundation · 2026</div>
          </div>
        </aside>
      </div>
    </main>
  );
}
